import { Suspense, useRef, useState } from 'react';
import type { FC } from 'react';
import { InView } from 'react-intersection-observer';

import type { PageViewTrackingEvent } from '@xing-com/crate-communication-tracking';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  trackPageviewModules,
  trackModuleVisibleAction,
  getNwtTrackingProps,
} from '@xing-com/crate-entity-pages-common/src/tracking';
import { PageError } from '@xing-com/crate-entity-pages-entity-pages-shared-components-page-error';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { ModuleContextProvider } from '@xing-com/crate-entity-pages-entity-pages-shared-context-module-context';
import { useModulesContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-modules-context';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useExperiment } from '@xing-com/hub';
import { GridContainer } from '@xing-com/xing-grid';

import { modulesMap as MODULES } from '../../config/modules-map';

import * as Styled from './modules-renderer.styles';

export type IntersectUseState = {
  [key: string]: boolean;
};

export type TrackedRef = {
  [key: string]: unknown;
  pageview?: boolean;
};
type Ratio = {
  [type: string]: number;
};

type ModulesRendererProps = {
  onEnterModule?: (type: string) => void;
  navigationOffset: number;
};
export const ModulesRenderer: FC<ModulesRendererProps> = ({
  onEnterModule = () => undefined,
  navigationOffset,
}) => {
  const { isEditing } = useEditContext();
  const { isLoggedIn } = useLoginState();
  const { pageContext } = usePageContext();
  const { pageModules, pageModulesByType } = useModulesContext();
  const { track: trackPageView } = useTracking<PageViewTrackingEvent>();

  const shouldHideKununuData = useExperiment('ABACUS-518') === 'B';

  const ratio = useRef<Ratio>({});
  const tracked = useRef<TrackedRef>({});
  const [intersected, setIntersected] = useState<IntersectUseState>({});

  const handleModuleIntersect = (
    event: IntersectionObserverEntry,
    type: string,
    index: number
  ) => {
    ratio.current = { ...ratio.current, [type]: event.intersectionRatio };

    setTimeout(() => {
      const maxValue = Math.max(...Object.values(ratio.current));
      const index =
        maxValue === 0
          ? -1
          : pageModulesByType.findIndex(
              (type) => ratio.current[type] === maxValue
            );

      onEnterModule(pageModulesByType[index]);
    }, 400);

    if (!tracked.current[type] && event.isIntersecting) {
      tracked.current = {
        ...tracked.current,
        [type]: true,
      };

      setIntersected({ ...intersected, [type]: true });
      trackModuleVisibleAction(type, index + 1);

      // NWT module in viewport
      trackPageView({
        type: 'nwt',
        application: 'wbm',
        event: 'entered_viewport',
        eventSchema: 'extended',
        sentBy: 'entity_pages',
        moduleTitle: type,
        modulePosition: index,
        ...getNwtTrackingProps({ pageContext, isEditing }),
      });
    }
  };

  const isValidContentPage =
    pageContext.isInsiderPage ||
    pageContext.isIndustryPage ||
    pageContext.isPremiumPage ||
    pageContext.isPublisherPage ||
    pageContext.isTopicPage ||
    pageContext.isGroupPage;

  if (!tracked.current['pageview'] && isEditing !== null) {
    tracked.current = {
      ...tracked.current,
      pageview: true,
    };

    trackPageviewModules({
      isLoggedIn: isLoggedIn,
      isEditor: pageContext.isEditor,
      isEditing: isEditing,
      modulesByType: pageModulesByType,
      id: pageContext.pageId?.split('.')?.[0],
      focusType: pageContext.focusType,
      contractType: pageContext.contractType,
      isFollowing: !!pageContext.isFollowingPage,
    });

    // nwt
    trackPageView({
      type: 'nwt',
      page: 'entity_pages/overview',
      application: 'wbm',
      event: 'viewed_screen',
      eventSchema: 'basic',
      sentBy: 'entity_pages',
      ...getNwtTrackingProps({ pageContext, isEditing }),
    });
  }

  return (
    <GridContainer data-testid="ENTITY-PAGES-MODULES-LIST">
      <Styled.Row>
        <Styled.Column size={12}>
          {pageModules.map((pageModulesItem, i) => {
            const { type, __typename } = pageModulesItem;
            const globalId =
              __typename === 'EntityPageGenericModule'
                ? pageModulesItem.globalId
                : undefined;
            const properties =
              __typename === 'EntityPageGenericModule'
                ? pageModulesItem.properties
                : undefined;

            if (!type) return null;
            if (type === 'kununu' && shouldHideKununuData) return null;

            const hasInsiderAboutModule =
              type === 'about_us' && isValidContentPage;
            const moduleDefinition = MODULES[type];

            if (!moduleDefinition) return null;

            const Module = hasInsiderAboutModule
              ? MODULES['about_content_pages']
              : (moduleDefinition ?? null);

            if (!Module) return null;

            const moduleContext = {
              moduleProperties: properties,
              intersected: type ? intersected[type] : null,
              moduleType: type,
              moduleGlobalId: globalId,
            };

            return (
              <ModuleContextProvider key={i} value={moduleContext}>
                <InView
                  onChange={(_inView, entry) =>
                    handleModuleIntersect(entry, type, i)
                  }
                  rootMargin={`-${navigationOffset}px 0px 0px 0px`}
                  threshold={[0, 0.2, 0.4, 0.6, 0.7, 1]}
                >
                  <Styled.ModuleWrapper
                    id={`module-${type}`}
                    data-testid={`ENTITY-PAGE-${type.toUpperCase()}-MODULE`}
                    data-cy={`${type}Module`}
                  >
                    <ErrorBoundary onError={() => <PageError />}>
                      <Suspense fallback={null}>
                        <Module key={i} />
                      </Suspense>
                    </ErrorBoundary>
                  </Styled.ModuleWrapper>
                </InView>
              </ModuleContextProvider>
            );
          })}
        </Styled.Column>
      </Styled.Row>
    </GridContainer>
  );
};
